import { useEffect, useState } from 'react';
import BackLink from '../components/BackLink';
import FranchiseeProfile from '../components/FranchiseeProfile';
import SatelliteFallbackBoundary from '../components/SatelliteFallbackBoundary';
import Tabs from '../components/Tabs';
import AdMatchTab from '../tabs/AdMatchTab';
import CallsAndLeadsTab from '../tabs/CallsAndLeadsTab';
import ContentTab from '../tabs/ContentTab';
import ImportantLinksTab from '../tabs/ImportantLinksTab';
import PaidPerformanceTab from '../tabs/PaidPerformanceTab';
import PerformanceTab from '../tabs/PerformanceTab';
import SocialTab from '../tabs/SocialTab';
import WebsiteTab from '../tabs/WebsiteTab';
import WelcomeHomeTab from '../tabs/WelcomeHomeTab';
import { useFranchiseesQuery } from '../api/queries';
import type { TabId } from '../types';
import { TABS } from '../types';

type Props = {
  af: string;
  initialTab?: TabId;
  onChangeTab: (tab: TabId) => void;
  /** When provided, renders a "← Back to {backHomeLabel}" link above the
   *  franchisee profile. Omitted for franchisee-role users who have no
   *  parent context to return to. */
  onBackHome?: () => void;
  backHomeLabel?: string;
};

function isReady(tab: TabId): boolean {
  return TABS.find((t) => t.id === tab)?.ready ?? false;
}

export default function FranchiseeDetail({
  af,
  initialTab,
  onChangeTab,
  onBackHome,
  backHomeLabel,
}: Props) {
  const [tab, setTab] = useState<TabId>(initialTab ?? 'tab-website');
  const { data: franchisees } = useFranchiseesQuery();
  const primaryAf = af.replace(/\.[0-9]+$/, '');

  useEffect(() => {
    if (initialTab && initialTab !== tab) setTab(initialTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  function handleTab(t: TabId) {
    setTab(t);
    onChangeTab(t);
  }

  return (
    <SatelliteFallbackBoundary
      af={af}
      primaryAf={primaryAf}
      render={(effectiveAf, fellBack) => {
        const selected = franchisees?.find((f) => f.af_number === effectiveAf) ?? null;
        return (
          <>
            {onBackHome && backHomeLabel && (
              <BackLink label={backHomeLabel} onClick={onBackHome} />
            )}
            {fellBack && (
              <div className="satellite-fallback-banner">
                Showing data for primary location <strong>{primaryAf}</strong> — the
                detail view for satellite <strong>{af}</strong> couldn't be displayed.
              </div>
            )}
            {selected && <FranchiseeProfile franchisee={selected} />}
            <Tabs active={tab} onChange={handleTab} />
            <main className="tab-content">
              {tab === 'tab-website' && <WebsiteTab franchisee={selected} />}
              {tab === 'tab-performance' && <PerformanceTab franchisee={selected} />}
              {tab === 'tab-paid' && <PaidPerformanceTab franchisee={selected} />}
              {tab === 'tab-calls' && <CallsAndLeadsTab af={effectiveAf} />}
              {tab === 'tab-admatch' && <AdMatchTab af={effectiveAf} />}
              {tab === 'tab-social' && <SocialTab af={effectiveAf} />}
              {tab === 'tab-welcomehome' && <WelcomeHomeTab af={effectiveAf} />}
              {tab === 'tab-content' && <ContentTab af={effectiveAf} />}
              {tab === 'tab-links' && <ImportantLinksTab />}
              {!isReady(tab) && (
                <div className="tab-placeholder">
                  <h3>Not yet ported</h3>
                  <p>This tab will be ported next.</p>
                </div>
              )}
            </main>
          </>
        );
      }}
    />
  );
}
